import { Card, CardBody, CardHeader, Heading, Text } from '@chakra-ui/react'
import { DDSTheme, ddsTokens } from '@ds/index'
import {
	Area,
	CartesianGrid,
	ComposedChart,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'

import { Granularity, MeetingBookedData, TimeFrame } from '../types'
import { groupSubmissionsByGranularity } from '../utils'
import ChartSpinner from './ChartSpinner'
import ChartTooltip from './ChartTooltip'

type Props = {
	meetingsBookedData: MeetingBookedData[] | undefined
	granularity: Granularity
	timeFrame: TimeFrame
	isLoading?: boolean
}

export const SubmissionsOverTime = ({
	meetingsBookedData,
	granularity,
	timeFrame,
	isLoading,
}: Props) => {
	const tokens = ddsTokens[DDSTheme.light]
	const processedData = groupSubmissionsByGranularity(
		meetingsBookedData || [],
		granularity,
		timeFrame,
	)

	const areaColor = tokens.primitive.light.primary[300]
	const lineColor = tokens.primitive.light.primary[500]

	return (
		<Card
			mx={6}
			shadow="sm"
			p={4}
			border={`1px solid ${tokens.primitive.light.gray[100]}`}
		>
			<CardHeader p={0} mb={2}>
				<Heading size="sm">Form Submissions Over Time</Heading>
				<Text fontSize="sm" color={tokens.primitive.light.gray[600]}>
					# of form submissions
				</Text>
			</CardHeader>
			<CardBody p={0} position="relative" flexGrow={1}>
				{isLoading && <ChartSpinner />}

				<ResponsiveContainer width="100%" height={250}>
					<ComposedChart
						data={processedData}
						margin={{ top: 24, right: 20, bottom: 0 }}
					>
						<CartesianGrid horizontal vertical={false} stroke="#F1F1F1" />
						<defs>
							<linearGradient
								id="submissionsGradient"
								x1="0"
								y1="0"
								x2="0"
								y2="1"
							>
								<stop offset="5%" stopColor={areaColor} stopOpacity={0.3} />
								<stop offset="95%" stopColor={areaColor} stopOpacity={0.05} />
							</linearGradient>
						</defs>
						<XAxis
							dataKey="week"
							tickLine={false}
							tick={{ fill: '#666666' }}
							dy={5}
						/>
						<YAxis
							tickLine={false}
							stroke="transparent"
							tick={{ fill: '#666666' }}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: tokens.primitive.light.white[100],
								borderColor: tokens.primitive.light.gray[200],
								color: tokens.primitive.light.gray[800],
							}}
							content={<ChartTooltip />}
						/>
						<Area
							type="monotone"
							dataKey="submissions"
							fill="url(#submissionsGradient)"
							fillOpacity={1}
							tooltipType="none"
							legendType="none"
							connectNulls
						/>
						<Line
							type="monotone"
							dataKey="submissions"
							stroke={lineColor}
							strokeWidth={2}
							dot={true}
							name="Submissions"
							connectNulls
						/>
					</ComposedChart>
				</ResponsiveContainer>
			</CardBody>
		</Card>
	)
}
